import { Redirect } from "@redotech/react-router-util/redirect";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { MarketingPermissions } from "@redotech/redo-model/permissions";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { lazy, memo, Suspense } from "react";
import { Navigate } from "react-router-dom";

const EmailSmsLandingPage = lazy(() =>
  import(
    "@redotech/redo-merchant-marketing-app/email-sms/email-sms-landing-page"
  ).then((m) => ({ default: m.EmailSmsLandingPage })),
);

const campaignDetailsBreadcrumb = breadcrumbSlot();

const EmailSmsDefaultRoute = memo(function EmailSmsDefaultRoute() {
  const team = useRequiredContext(TeamContext);
  const marketingEnabled = team.settings.marketing?.enabled;
  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );
  const canManageCampaigns = useHasPermission(
    MarketingPermissions.MANAGE_CAMPAIGNS,
  );

  if (!marketingEnabled) {
    return <Navigate replace to="welcome" />;
  }

  if (canViewMarketing) {
    return (
      <Suspense fallback={null}>
        <EmailSmsLandingPage />
      </Suspense>
    );
  }

  if (canManageCampaigns) {
    return <Navigate replace to="campaigns" />;
  }

  return <Navigate replace to="welcome" />;
});

export const backwardsCompatibleEmailSmsRoutes: PermissionedRouteObject[] = [
  {
    path: "automations",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect
                match={/automations/}
                replaceValue="email-sms/automations"
              />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect
                match={/automations/}
                replaceValue="email-sms/automations"
              />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "campaigns",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect
                match={/campaigns/}
                replaceValue="email-sms/campaigns"
              />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect
                match={/campaigns/}
                replaceValue="email-sms/campaigns"
              />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "templates",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect
                match={/templates/}
                replaceValue="email-sms/templates"
              />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect
                match={/templates/}
                replaceValue="email-sms/templates"
              />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "audience",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/audience/} replaceValue="email-sms/audience" />
            ),
          },
          {
            path: "*",

            element: (
              <Redirect match={/audience/} replaceValue="email-sms/audience" />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "segments",
    children: [
      {
        children: [
          {
            index: true,

            element: (
              <Redirect match={/segments/} replaceValue="email-sms/segments" />
            ),
          },
          {
            path: "*",

            element: (
              <Redirect match={/segments/} replaceValue="email-sms/segments" />
            ),
          },
        ],
      },
    ],
  },
];

export const emailSmsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Email & SMS", breadcrumbNavigate: false },
    children: [
      { index: true, element: <EmailSmsDefaultRoute /> },
      {
        path: "ai-personas",
        handle: { breadcrumb: "AI personas" },
        children: [
          {
            index: true,
            async lazy() {
              const { AiPersonasPage } = await import(
                "@redotech/redo-merchant-marketing-app/ai-personas/ai-personas-page"
              );
              return { Component: AiPersonasPage };
            },
          },
        ],
      },
      {
        path: "signups",
        handle: { breadcrumb: "Sign-ups" },
        children: [
          {
            index: true,
            async lazy() {
              const { MarketingSignups } = await import(
                "@redotech/redo-merchant-marketing-app/signups/signups-page"
              );
              return { Component: MarketingSignups };
            },
          },
          {
            path: "builder/:signupFormId",
            handle: { breadcrumb: "Sign-up Builder" },
            async lazy() {
              const { MarketingSignupBuilder } = await import(
                "@redotech/redo-merchant-marketing-app/signups/signup-form-builder"
              );
              return { Component: MarketingSignupBuilder };
            },
          },
        ],
      },
      // Mount the spotlight builder under marketing so the email-sms
      // sidebar's "Spotlights" link (shown when
      // team.settings.marketing.spotlightsEnabled) keeps the user inside
      // the marketing area instead of routing into /conversion/... and
      // swapping out the side nav. Same component tree as the conversion
      // mount — they share the same builder.
      {
        path: "spotlights",
        handle: { breadcrumb: "Spotlights" },
        children: [
          {
            index: true,
            async lazy() {
              const { SpotlightsPage } = await import(
                "../spotlights/spotlights-page"
              );
              return { Component: SpotlightsPage };
            },
          },
          {
            path: "new",
            async lazy() {
              const { SpotlightTemplateSelection } = await import(
                "../spotlights/spotlight-template-selection"
              );
              return { Component: SpotlightTemplateSelection };
            },
          },
          {
            path: "builder/:spotlightId",
            handle: { breadcrumb: "Spotlight builder" },
            async lazy() {
              const { SpotlightBuilder } = await import(
                "../spotlights/spotlight-builder"
              );
              return { Component: SpotlightBuilder };
            },
          },
        ],
      },
      {
        path: "campaigns",
        handle: { breadcrumb: "Campaigns" },
        children: [
          {
            index: true,
            async lazy() {
              const { MarketingCampaigns } = await import(
                "@redotech/redo-merchant-marketing-app/campaigns/campaigns-page"
              );
              return { Component: MarketingCampaigns };
            },
          },
          {
            path: ":campaignId/details/:tab?",
            handle: { breadcrumb: campaignDetailsBreadcrumb },
            async lazy() {
              const { MarketingCampaignDetails } = await import(
                "@redotech/redo-merchant-marketing-app/campaigns/campaign-details-page"
              );
              return {
                element: (
                  <MarketingCampaignDetails
                    breadcrumb={campaignDetailsBreadcrumb}
                  />
                ),
              };
            },
          },
          {
            path: ":campaignId/edit/groups",
            lazy: async () => {
              const { EditCampaignGroups } = await import(
                "@redotech/redo-merchant-marketing-app/campaigns/edit/groups"
              );
              return { Component: EditCampaignGroups };
            },
          },
          {
            path: ":campaignId/edit/template",
            lazy: async () => {
              const { EditCampaignTemplate } = await import(
                "@redotech/redo-merchant-marketing-app/campaigns/edit/template"
              );
              return { Component: EditCampaignTemplate };
            },
          },
          {
            path: ":campaignId/edit/preview", // called "review" in the mocks but I want to disambiguate from the "reviews" product
            lazy: async () => {
              const { PreviewCampaign } = await import(
                "@redotech/redo-merchant-marketing-app/campaigns/edit/preview"
              );
              return { Component: PreviewCampaign };
            },
          },
          {
            path: ":campaignId/edit-with-ai/template",
            handle: { breadcrumb: "AI Campaign Builder" },
            lazy: async () => {
              const { AiEmailCampaignBuilderPage: AiEmailBuilderPage } =
                await import(
                  "@redotech/redo-merchant-marketing-app/ai/pages/ai-email-campaign-builder-page"
                );
              return { Component: AiEmailBuilderPage };
            },
          },
        ],
      },
      {
        path: "templates",
        handle: { breadcrumb: "Templates" },
        children: [
          {
            path: "email",
            handle: { breadcrumb: "Email library" },
            children: [
              {
                index: true,
                async lazy() {
                  const { EmailLibraryPage } = await import(
                    "@redotech/redo-merchant-marketing-app/email-builder/email-library-page"
                  );
                  return { Component: EmailLibraryPage };
                },
              },
              {
                path: "managed/:emailTemplateId",
                handle: { breadcrumb: "Managed Template" },
                lazy: async () => {
                  const { EmailBuilderForTemplateCollection } = await import(
                    "@redotech/redo-merchant-marketing-app/email-builder/email-builder"
                  );
                  return { Component: EmailBuilderForTemplateCollection };
                },
              },
              {
                path: ":emailTemplateId",
                handle: { breadcrumb: "Edit" },
                lazy: async () => {
                  const { EmailBuilderForTemplateCollection } = await import(
                    "@redotech/redo-merchant-marketing-app/email-builder/email-builder"
                  );
                  return { Component: EmailBuilderForTemplateCollection };
                },
              },
            ],
          },
          {
            path: "click-map/:templateId",
            handle: { breadcrumb: "Click Map" },
            async lazy() {
              const { FullscreenClickMap } = await import(
                "@redotech/redo-merchant-marketing-app/email-builder/click-map/fullscreen-click-map"
              );
              return { Component: FullscreenClickMap };
            },
          },
          {
            path: "sms",
            handle: { breadcrumb: "SMS" },
            children: [
              {
                index: true,
                async lazy() {
                  const { SmsBuilderTemplates } = await import(
                    "@redotech/redo-merchant-marketing-app/sms-builder/sms-builder-templates"
                  );
                  return { Component: SmsBuilderTemplates };
                },
              },
              {
                path: ":smsTemplateId",
                async lazy() {
                  const { SmsBuilderForAutomations } = await import(
                    "@redotech/redo-merchant-marketing-app/sms-builder/sms-builder"
                  );
                  return { Component: SmsBuilderForAutomations };
                },
              },
            ],
          },
        ],
      },
      {
        path: "welcome",
        handle: { breadcrumb: "Welcome" },
        children: [
          {
            index: true,
            async lazy() {
              const { MarketingWelcomePage } = await import(
                "@redotech/redo-merchant-marketing-app/welcome/marketing-welcome-page"
              );
              return { Component: MarketingWelcomePage };
            },
          },
        ],
      },
      {
        path: "audience",
        handle: { breadcrumb: "Audience" },
        children: [
          {
            index: true,
            async lazy() {
              const { AudiencePage } = await import(
                "@redotech/redo-merchant-marketing-app/audience/audience-page"
              );
              return { Component: AudiencePage };
            },
          },
          {
            handle: { breadcrumb: "Customer Detail" },
            path: "customers/:customerId",
            async lazy() {
              const { CustomerDetail } = await import(
                "@redotech/redo-merchant-customer-app/customer-detail/customer-detail"
              );
              return { element: <CustomerDetail /> };
            },
          },
        ],
      },
      {
        path: "deliverability",
        handle: { breadcrumb: "Deliverability" },
        children: [
          {
            index: true,
            async lazy() {
              const { DeliverabilityReport } = await import(
                "@redotech/redo-merchant-marketing-app/deliverability/deliverability-page"
              );
              return { Component: DeliverabilityReport };
            },
          },
        ],
      },
      {
        path: "segments",
        handle: { breadcrumb: "Segments" },
        children: [
          {
            index: true,
            async lazy() {
              const { SegmentsPage } = await import(
                "@redotech/redo-merchant-marketing-app/segments/segments-page"
              );
              return { Component: SegmentsPage };
            },
          },
          {
            path: ":segmentId",
            async lazy() {
              const { SegmentBuilderPage } = await import(
                "@redotech/redo-merchant-marketing-app/segments/segment-builder-page"
              );
              return { Component: SegmentBuilderPage };
            },
          },
        ],
      },
    ],
  },
];

export const automationRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Automations" },
    children: [
      {
        index: true,
        async lazy() {
          const { AutomationsPage: MarketingAutomations } = await import(
            "@redotech/redo-merchant-marketing-app/automations/automations-page"
          );
          return { Component: MarketingAutomations };
        },
      },
      {
        path: "managed/:automationId",
        handle: { breadcrumb: "Managed Automation" },
        async lazy() {
          const { MarketingAutomationBuilder } = await import(
            "./automations/marketing-automation-builder"
          );
          return { Component: MarketingAutomationBuilder };
        },
      },
      {
        path: ":automationId",
        handle: { breadcrumb: "Automation Builder" },
        async lazy() {
          const { MarketingAutomationBuilder } = await import(
            "./automations/marketing-automation-builder"
          );
          return { Component: MarketingAutomationBuilder };
        },
      },
    ],
  },
];

export const loyaltyRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Loyalty", breadcrumbNavigate: false },
    children: [
      {
        path: "program",
        handle: { breadcrumb: "Points program" },
        async lazy() {
          const { LoyaltyProgram } = await import(
            "@redotech/redo-merchant-marketing-app/loyalty/program"
          );
          return { element: <LoyaltyProgram /> };
        },
      },
      {
        path: "promotions",
        handle: { breadcrumb: "Promotions" },
        async lazy() {
          const { LoyaltyPromotions } = await import(
            "@redotech/redo-merchant-marketing-app/loyalty/promotions/promotions"
          );
          return { element: <LoyaltyPromotions /> };
        },
      },
      {
        path: "members",
        handle: { breadcrumb: "Members" },
        async lazy() {
          const { LoyaltyMembers } = await import(
            "@redotech/redo-merchant-marketing-app/loyalty/loyalty-members"
          );
          return { Component: LoyaltyMembers };
        },
      },
      {
        path: "popup/customize",
        handle: { breadcrumb: "Popup" },
        async lazy() {
          const { LoyaltyPopupPage } = await import(
            "@redotech/redo-merchant-marketing-app/loyalty/loyalty-popup/popup-page"
          );
          return { Component: LoyaltyPopupPage };
        },
      },
      {
        path: "page/customize",
        handle: { breadcrumb: "Loyalty Page Builder" },
        async lazy() {
          const { LoyaltyPageBuilderPage } = await import(
            "@redotech/redo-merchant-marketing-app/loyalty/page-builder/loyalty-page-builder"
          );
          return { Component: LoyaltyPageBuilderPage };
        },
      },
      {
        path: "on-site",
        handle: { breadcrumb: "On-site" },
        children: [
          {
            index: true,
            async lazy() {
              const { LoyaltyOnSitePage } = await import(
                "@redotech/redo-merchant-marketing-app/loyalty/on-site-page"
              );
              return { Component: LoyaltyOnSitePage };
            },
          },
          {
            path: "loyalty-program-page/guide",
            handle: { breadcrumb: "Implementation Guide" },
            async lazy() {
              const { LoyaltyProgramPageGuide } = await import(
                "@redotech/redo-merchant-marketing-app/loyalty/guides/loyalty-program-page-guide"
              );
              return { Component: LoyaltyProgramPageGuide };
            },
          },
          {
            path: "shopify-checkout-widget/guide",
            handle: { breadcrumb: "Implementation Guide" },
            async lazy() {
              const { ShopifyCheckoutWidgetGuide } = await import(
                "@redotech/redo-merchant-marketing-app/loyalty/guides/shopify-checkout-widget-guide"
              );
              return { Component: ShopifyCheckoutWidgetGuide };
            },
          },
          {
            path: "loyalty-popup/guide",
            handle: { breadcrumb: "Implementation Guide" },
            async lazy() {
              const { LoyaltyPopupGuide } = await import(
                "@redotech/redo-merchant-marketing-app/loyalty/guides/loyalty-popup-guide"
              );
              return { Component: LoyaltyPopupGuide };
            },
          },
        ],
      },
    ],
  },
];
